import type {ReactElement} from 'react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {
  DisplayType,
  ProductType,
  ThumbNailType,
  Status,
  PublishingAnalyticsAction,
  type PosterProductDataForProcessDesign,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {useDispatch} from 'react-redux';
import styles from './publishing-digital-signage-list.module.scss';
import {
  selectionTypeSubDisplay,
  setOrderDetails,
  setProductType,
  subPanelFlow,
  setShowLoader,
  setPosterPageHashedId,
  setPosterPreviewUrl,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import {ModalDetails} from '@Components/modal/modal-context';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import type {PublishingOptions} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {useHashedId, usePosterProduct, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {PublishingDigitalSignageListProps} from './publishing-digital-signage-list.types';
import {getDefaultOrderDetails, getDefaultPosterProductDataForProcessDesign, useProductUsed} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {GA4EventParamName} from '@Libraries/ga-events';
import {processDesignForPublishing} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {PosterPagesCarousel} from '@Components/poster-preview-container/components/poster-pages-carousel/poster-pages-carousel';
import type {PosterPageMeta} from '@Components/poster-preview-container/components/poster-pages-carousel/poster-pages-carousel.types';
import type {PosterPagePreviewDataResponse, PosterPagePreviewInfo} from '@Components/poster-preview-container/poster-preview-container.types';
import {pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';

export function PublishingDigitalSignageList({displayType, setTopShadow}: PublishingDigitalSignageListProps): ReactElement<any> {
  const dispatch = useDispatch();
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const isFirstRender = useRef(true);
  const signageContentRef = useRef<HTMLDivElement>(null);
  const [designProcessClicked, setDesignProcessClicked] = useState(false);
  const premiumProductDataForPoster = usePosterProduct();
  const hashedId = useHashedId();
  const showLoader = useShowLoader();
  const {topShadow} = useTopBottomScrollShadow(signageContentRef);
  const [isCarouselLoading, setIsCarouselLoading] = useState<boolean>(true);
  const [pageMetas, setPageMetas] = useState<PosterPageMeta[]>([]);
  const [selectedPageNumber, setSelectedPageNumber] = useState<number>(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [pageHashedId, setPageHashedId] = useState<string>('');

  useEffect(() => {
    setTopShadow(topShadow);
  }, [topShadow]);

  useEffect(() => {
    if (!hashedId) {
      setIsCarouselLoading(false);
      return;
    }

    setIsCarouselLoading(true);
    pollForPosterPreviewGenerationStatus(
      hashedId,
      () => {
        void fetchPosterPagePreviewData();
      },
      (error) => {
        console.error(error);
        setIsCarouselLoading(false);
      }
    );
  }, [hashedId]);

  const fetchPosterPagePreviewData = async (): Promise<void> => {
    try {
      const response = (await window.PMW.readLocal('poster/getPosterPagePreviewDataForCarousel', {
        posterHashedId: hashedId,
      })) as PosterPagePreviewDataResponse;

      const convertedPageMetas: PosterPageMeta[] = response.pagesInfo.map((page: PosterPagePreviewInfo, index: number) => ({
        pageIndex: index,
        isVideo: page.isVideo,
        previewThumbnailUrl: page.thumbnailUrl,
        previewUrl: page.screenUrl,
        subtitle: window.i18next.t('pmwjs_page_number', {pageNumber: index + 1}),
        pageHashedId: page.pageHashedId,
      }));

      if (convertedPageMetas.length > 0) {
        setPreviewUrl(convertedPageMetas[0].previewUrl);
        setPageHashedId(convertedPageMetas[0].pageHashedId ?? '');
      }
      setPageMetas(convertedPageMetas);
    } catch (error) {
      console.error(error);
    } finally {
      setIsCarouselLoading(false);
    }
  };

  const productUsed = useProductUsed();
  const status = productUsed?.status ?? '';
  const isRegenarationDueToDeletionNeed = productUsed?.needsRegenerationDueToDeletion ?? false;

  let productPrice = '';
  if (productUsed?.needsPurchase) {
    productPrice = productUsed?.price ?? '';
  }

  const isOrderOutdated = productUsed?.status === Status.OUTDATED;
  const wasPreviouslyPurchased = productUsed?.purchaseId && Number(productUsed.purchaseId) > 0;
  const showFreeEditsUsedUpBanner = productUsed?.needsPurchase && wasPreviouslyPurchased;

  useEffect(() => {
    if (isFirstRender.current) {
      dispatch(
        setProductType(JSON.stringify(premiumProductDataForPoster) !== JSON.stringify(getDefaultPosterProductDataForProcessDesign()) ? ProductType.POSTER : ProductType.PRINT)
      );
      dispatch(setOrderDetails(getDefaultOrderDetails()));
      isFirstRender.current = false;
    }
  }, []);

  const savePosterPublishingInfoInServer = (): void => {
    void window.PMW.writeLocal('poster/savePublishingInfo', {
      hid: hashedId,
      pageHashedId,
    });
  };

  const processDesign = (): void => {
    dispatch(setShowLoader(true));
    processDesignForPublishing(productUsed ?? ({} as PosterProductDataForProcessDesign));
    setDesignProcessClicked(true);
  };

  useEffect(() => {
    if (!showLoader && status === Status.DOWNLOADABLE && !isRegenarationDueToDeletionNeed && designProcessClicked) {
      dispatch(subPanelFlow());
    }
  }, [showLoader, status, designProcessClicked, isRegenarationDueToDeletionNeed]);

  const publishingOptionsImages: PublishingOptions[] = [
    {
      id: SubFlowType.SMART_TV_SUBDISPLAY,
      iconName: 'icon-tv',
      heading: window.i18next.t('pmwjs_play_on_smart_tv'),
      thumbNailType: ThumbNailType.ICON,
      subFlowType: SubFlowType.SMART_TV_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'PlayOnSmartTV'});
      },
    },
    {
      id: SubFlowType.RAYDIANT_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_raydiant'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-displai.png',
      subFlowType: SubFlowType.RAYDIANT_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Displai'});
      },
    },
    {
      id: SubFlowType.SCREEN_CLOUD_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_screencloud'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-screencloud.png',
      subFlowType: SubFlowType.SCREEN_CLOUD_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'ScreenCloud'});
      },
    },
    {
      id: SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_signagelive_widget'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-signagelive.png',
      subFlowType: SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'SignageLive'});
      },
    },
    {
      id: SubFlowType.POSTERBOOKING_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_posterbooking'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-poster-booking.png',
      subFlowType: SubFlowType.POSTERBOOKING_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'PosterBooking'});
      },
    },
    {
      id: SubFlowType.FUGO_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_fugo'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-fugo.png',
      subFlowType: SubFlowType.FUGO_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Fugo'});
      },
    },
    {
      id: SubFlowType.YODECK_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_yodeck'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-yodeck.png',
      subFlowType: SubFlowType.YODECK_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Yodeck'});
      },
    },
    {
      id: SubFlowType.CLEAR_DIGITAL_SHO_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: 'Clear Digital Sho',
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-clear-digital-sho.png',
      subFlowType: SubFlowType.CLEAR_DIGITAL_SHO_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Clear Digital Sho'});
      },
    },
    /**
     * 6th December 2024 - Disabling VIZROM since their site is down (indefinitely)
     */
    // {
    //   id: SubFlowType.VIZROM_SUBDISPLAY,
    //   iconName: 'icon-share-external',
    //   heading: window.i18next.t('pmwjs_vizrom'),
    //   thumbNailType: ThumbNailType.IMAGE,
    //   imgSrc: 'publishing-vizrom.png',
    //   subFlowType: SubFlowType.VIZROM_SUBDISPLAY,
    //   price: productPrice,
    // gaTracking: (): void => {
    // window.PMW.gtm.trackGA4CustomEvent(AnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Vizrom'});
    // },
    // },
    /**
     * 29th September 2025 - Dripboards option is disabled due to an internal issue at their end
     */
    // {
    //   id:  SubFlowType.DRIPBOARDS_SUBDISPLAY,
    //   iconName: 'icon-share-external',
    //   heading: window.i18next.t('pmwjs_dripboards'),
    //   thumbNailType: ThumbNailType.IMAGE,
    //   imgSrc: 'publishing-dripboards.png',
    //   subFlowType: SubFlowType.DRIPBOARDS_SUBDISPLAY,
    //   price: productPrice,
    // },
  ];

  let panelStyle = '';
  if (inMobile) {
    panelStyle = `spacing-p-3 ${styles.publishingOptionsGapPanelMobile}`;
  } else {
    panelStyle = displayType === DisplayType.MODAL ? styles.publishingOptionsGapImagePanel : `flex-v-row spacing-p-3 ${styles.publishingOptionsGapImageDropDown}`;
  }

  const onSignageItemClick = (option: PublishingOptions): void => {
    if (option.gaTracking) {
      option.gaTracking();
    }
    savePosterPublishingInfoInServer();
    dispatch(setPosterPageHashedId(pageHashedId));
    dispatch(selectionTypeSubDisplay(option.subFlowType ?? SubFlowType.DEFAULT));
    dispatch(setPosterPreviewUrl(previewUrl));
    processDesign();
  };

  const getPlaceholderPageMetas = (): PosterPageMeta[] => {
    const PLACEHOLDER_PAGE_COUNT = 2;
    return Array.from({length: PLACEHOLDER_PAGE_COUNT}, (_, index) => ({
      pageIndex: index,
      isVideo: false,
      previewThumbnailUrl: '',
      previewUrl: '',
      subtitle: '',
    }));
  };

  const handlePageThumbnailClick = (selectedIndex: number, posterPreviewUrl: string, pageHashedId?: string): void => {
    setSelectedPageNumber(selectedIndex + 1);
    setPreviewUrl(posterPreviewUrl);
    setPageHashedId(pageHashedId ?? '');
  };

  const dropdownPopup = (displayPageMetas: PosterPageMeta[]): ReactElement => {
    return (
      <div className={styles.dropdownPopupContent}>
        <Text className="content-body spacing-m-0 -text-align-left" size={TextSize.MD} val={window.i18next.t('pmwjs_select_a_page')} bold />
        <div className={styles.carouselWrapper}>
          <PosterPagesCarousel
            pageMetas={displayPageMetas}
            isLoading={isCarouselLoading}
            preSelectedPageIndex={selectedPageNumber - 1}
            showSubtitles={true}
            disableCenterAlignment={true}
            customCarouselClasses={styles.carousel}
            onPageThumbnailClick={handlePageThumbnailClick}
          />
        </div>
      </div>
    );
  };

  const renderDropdown = (): ReactElement => {
    const displayPageMetas = isCarouselLoading ? getPlaceholderPageMetas() : pageMetas;

    const dropdownSelector = (
      <DropdownGhostSelectBox
        className={`flexbox flex-align-items-center gap-1 spacing-p-2 content-body ${styles.rightSection} ${isDropdownOpen ? styles.dropdownSelectorOpen : ''}`}
        text={selectedPageNumber.toString()}
        customIconSize={IconSize.SIZE_ICON_20}
        isBold
      />
    );

    return (
      <Dropdown
        className={styles.dropdownContainer}
        selector={dropdownSelector}
        doCloseOnInnerClick={false}
        popUpHasCustomWidth={true}
        popupClassName={styles.dropdownPopup}
        popup={dropdownPopup(displayPageMetas)}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        onOpen={() => {
          setIsDropdownOpen(true);
        }}
        onClose={() => {
          setIsDropdownOpen(false);
        }}
      />
    );
  };

  const renderPageSelector = (): ReactElement | null => {
    if (!window.PMW.enableMyStuffMultipagesUI || pageMetas.length <= 1) {
      return null;
    }

    return (
      <div className="flex-justify-between flex-align-items-center spacing-p-t-3 spacing-p-r-3 spacing-p-l-3 _full-width">
        <div className={`flexbox flex-align-items-center ${styles.leftSection}`}>
          <Icon icon="icon-pages" className="content-body" size={IconSize.SIZE_ICON_20} />
          <Text className="content-body" size={TextSize.MD} val={window.i18next.t('pmwjs_page')} bold />
        </div>
        {renderDropdown()}
      </div>
    );
  };

  const renderFreeEditsBanner = (): ReactElement | null => {
    if (!showFreeEditsUsedUpBanner) {
      return null;
    }

    return (
      <div className={`${styles.freeEditsContainer} flex-row-align-center spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 spacing-m-l-3 spacing-m-r-3 spacing-m-t-3`}>
        <Icon className="content-link spacing-m-r-1" icon="icon-info" size={IconSize.SIZE_ICON_16} />
        <Text className="content-link" val={window.i18next.t('pmwjs_free_edits_used_up')} size={TextSize.MD} />
      </div>
    );
  };

  const getPanelContent = (): ReactElement => {
    if (isCarouselLoading) {
      return (
        <div className={styles.signageDropDownHeight}>
          <PublishingLoaderScreen customHeight={styles.loaderHeight} loaderSize={LOADER_SIZE.LARGE} />
        </div>
      );
    }

    const notAvailableForMultipagesWarning = (
      <div className={`flexbox flex-align-items-center gap-1 ${styles.multiPageDisabledText}`}>
        <Icon icon="icon-exclamation-circle" size={IconSize.SIZE_ICON_16} className="content-disabled-dark" />
        <Text val={window.i18next.t('pmwjs_not_available_for_multiple_pages')} className="body-sm-regular content-disabled-dark" />
      </div>
    );

    return (
      <div ref={signageContentRef} className={`animated fadeIn ${styles.signageDropDownHeight}`}>
        {renderFreeEditsBanner()}
        {renderPageSelector()}
        <div className={panelStyle}>
          {publishingOptionsImages.map((option: PublishingOptions) => {
            const isNonFirstPageSelected = !isCarouselLoading && pageMetas.length > 1 && selectedPageNumber !== 1;
            const isPageSelectionPending = isCarouselLoading || (pageMetas.length > 0 && pageHashedId === '');
            const isDisplaiOrFugo = option.subFlowType === SubFlowType.RAYDIANT_SUBDISPLAY || option.subFlowType === SubFlowType.FUGO_SUBDISPLAY;
            const isDisabled = isPageSelectionPending || (isNonFirstPageSelected && isDisplaiOrFugo);

            return (
              <PublishingOptionListItem
                key={option.id}
                iconName={option.iconName}
                heading={option.heading}
                thumbNailType={option.thumbNailType}
                imgSrc={option.imgSrc}
                showTwoButtons={option.subFlowType === SubFlowType.SMART_TV_SUBDISPLAY && isOrderOutdated}
                customThumbnailClasses="bg-neutral-3 content-body"
                price={isDisabled ? '' : option.price}
                isDisabled={isDisabled}
                className={isDisabled ? styles.disabledOption : ''}
                afterHeadingTextContentBelow={isDisabled ? notAvailableForMultipagesWarning : undefined}
                onClick={() => {
                  onSignageItemClick(option);
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return showLoader ? <PublishingProgressBar /> : getPanelContent();
}
